import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  BreadcrumbArray,
  Button,
  Checkbox,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DirectionIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  entryFromEventFragment,
  Input,
  Label,
  LabelPill,
  PrimaryFullScreenSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  StatusBase,
  StatusUI,
  TimeLog,
  TransferGroupDescription,
  Txt,
  toast,
  UserProfile,
} from "@repo/ui";
import {
  formatDateComponentsInTimeZone,
  formatLocalDateComponents,
  formatLocalDateShortDay,
  getOpsTimezone,
  NotFoundError,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle } from "@untitledui/icons";
import Decimal from "decimal.js";
import { MoreVertical } from "lucide-react";
import { DateTime } from "luxon";
import { type FC, Suspense, useCallback, useState } from "react";
import { RiArrowLeftFill } from "react-icons/ri";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { fXTradeTransferGroupKindValues } from "~/gql/client/enumValues";
import type {
  FxTradeMarginTransactionFragment,
  FxTradeStatus,
  FxTradeTransferGroupKind,
  FxTradeTransferGroupLinkDetailFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fx-trades_/$fxTradeId")({
  head: () => opsHead("FX Trade"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  if (!params.fxTradeId) {
    throw new NotFoundError();
  }

  graphql(`
    fragment FxTradeTransferGroupLinkDetail on FXTradeTransferGroupLink {
      id
      kind
      transferGroup {
        id
        shortRef
        kind
        status
        createdAt
        createdByUser {
          ...UserProfileUI
        }
        totalVolume {
          amount
          currency
        }
        transfers {
          id
        }
        payouts {
          id
        }
        ...TransferGroupDescriptionFields
      }
    }

    fragment FxTradeMarginTransaction on Transaction {
      id
      status
      shortRef
      effectiveAt
      currencyAmount {
        amount
        currency
      }
      description
      direction
    }
  `);

  const { data, error } = useSuspenseQuery(
    graphql(
      `query FXTradeDetailPage($id: ID!) {
        fxTrade (id: $id) {
          id
          ...AdminFXTradeDetail
        }
      }

      fragment AdminFXTradeDetail on FXTrade {
        id
        buyToSellRate
        sellToBuyRate
        valueDate
        tradeType
        amount {
          amount
          currency
        }
        buyAmount {
          amount
          currency
        }
        sellAmount {
          amount
          currency
        }
        recordedRevenue {
          amount
          currency
        }
        createdAt
        buyCurrency
        externalId
        operation
        businessEntity {
          id
          name
          organisation {
            id
            name
          }
        }
        processAfterDateTime
        sellCurrency
        spreadBps
        status
        fxHedge {
          id
          openedDate
          maturityDate
          marginAmount {
            amount
            currency
          }
          markToMarket {
            id
            latestSellToBuyRate {
              id
              rate
            }
            latestBuyToSellRate {
              id
              rate
            }
            sellAmount {
              amount
              currency
            }
            buyAmount {
              amount
              currency
            }
            percentage
          }
        }
        transferGroupLinks {
          ...FxTradeTransferGroupLinkDetail
        }
        events {
          ...TimeLogEventEntry
        }
        ... on EburyFXTrade {
          marginTransactions {
            ... FxTradeMarginTransaction
          }
          eburyTradeDetails {
            id
            status
            feeAmount {
              amount
              currency
            }
            maturityDate
            ... on EburyFixedForwardDetails {
              marginCall {
                amount
                currency
              }
              initialMargin {
                amount
                currency
              }
              buyBalanceForDrawdown {
                amount
                currency
              }
              sellBalanceForDrawdown {
                amount
                currency
              }
            }
            ... on EburyWindowForwardDetails {
              marginCall {
                amount
                currency
              }
              initialMargin {
                amount
                currency
              }
              buyBalanceForDrawdown {
                amount
                currency
              }
              sellBalanceForDrawdown {
                amount
                currency
              }
            }
          }
        }
        ... on CurrencycloudFXTrade {
          ... @defer {
            currencycloudTradeDetails {
              id
              status
              shortReference
              midMarketRate
              coreRate
              partnerRate
              clientRate
            }
          }
        }
      }
      `,
    ),
    {
      variables: {
        id: params.fxTradeId,
      },
    },
  );

  if (!data || !data.fxTrade) {
    throw new NotFoundError();
  }

  const fxTrade = data.fxTrade;
  if (!fxTrade.businessEntity?.organisation) {
    throw new Error("expected business entity and organisation");
  }

  let fxProvider;
  switch (fxTrade.__typename) {
    case "EburyFXTrade":
      fxProvider = "Ebury";
      break;
    case "CurrencycloudFXTrade":
      fxProvider = "Visa";
      break;
    default:
      neverFunc(fxTrade);
  }

  const fxHedge = fxTrade.fxHedge;
  const markToMarket = fxHedge?.markToMarket;

  const forward =
    fxTrade.__typename === "EburyFXTrade" &&
    (fxTrade.eburyTradeDetails?.__typename === "EburyFixedForwardDetails" ||
      fxTrade.eburyTradeDetails?.__typename === "EburyWindowForwardDetails")
      ? fxTrade.eburyTradeDetails
      : undefined;

  const marginTransactions =
    fxTrade.__typename === "EburyFXTrade" &&
    (fxTrade.eburyTradeDetails?.__typename === "EburyFixedForwardDetails" ||
      fxTrade.eburyTradeDetails?.__typename === "EburyWindowForwardDetails")
      ? fxTrade.marginTransactions
      : undefined;

  return (
    <AdminPageContainer testId="admin-fx-trade-detail">
      <div className="flex items-center justify-between">
        <AdminPageHeader title="FX Trade" />
      </div>
      <BreadcrumbArray
        stack={[
          { title: "FX Trades", to: "/fx-trades" },
          { title: <span className="font-mono">{fxTrade.id}</span> },
        ]}
      />

      <div className="mt-4 flex flex-col gap-12">
        <div className="rounded-md border p-4">
          <div className="flex w-full items-start justify-between">
            <Txt variant="card-title">Trade details</Txt>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <FXTradeActionButton
                  fxTradeId={fxTrade.id}
                  status="CANCELLED"
                  triggerButtonLabel="Cancel FX Trade"
                  confirmTitle="Confirm cancellation"
                  confirmDescription="This will mark the FX trade as cancelled. Note that this will NOT update the trade with the provider."
                  disabled={
                    fxTrade.status === "CANCELLED" ||
                    fxTrade.status === "VOIDED"
                  }
                />
                <FXTradeActionButton
                  fxTradeId={fxTrade.id}
                  status="VOIDED"
                  triggerButtonLabel="Void FX Trade"
                  confirmTitle="Confirm void"
                  confirmDescription="This will mark the FX trade as voided. Note that this will NOT update the trade with the provider."
                  disabled={
                    fxTrade.status === "CANCELLED" ||
                    fxTrade.status === "VOIDED"
                  }
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <DetailRow
            label="Organisation"
            className="last:border-b-0"
            value={
              <Link
                to={`/organisations/${fxTrade.businessEntity.organisation.id}`}
              >
                {fxTrade.businessEntity.organisation.name}
              </Link>
            }
          />
          <DetailRow
            label="Business entity"
            className="last:border-b-0"
            value={
              <Link
                to={`/organisations/${fxTrade.businessEntity.organisation.id}/business-entities/${fxTrade.businessEntity.id}`}
              >
                {fxTrade.businessEntity.name}
              </Link>
            }
          />
          <DetailRow
            label="Trade type"
            className="last:border-b-0"
            value={prettyPrintEnum(fxTrade.tradeType)}
          />
          <DetailRow
            label="Status"
            className="last:border-b-0"
            value={<StatusUI status={fxTrade.status} />}
          />
          <DetailRow
            label="Trade created at"
            className="last:border-b-0"
            value={<DateUI date={fxTrade.createdAt} variant="DATETIME_TZ" />}
          />
          <DetailRow
            label="Value date"
            className="last:border-b-0"
            value={formatLocalDateShortDay(
              DateTime.fromISO(fxTrade.valueDate).toJSDate(),
            )}
          />
          <DetailRow
            label="Provider"
            className="last:border-b-0"
            value={fxProvider}
          />
          <DetailRow
            label="Operation"
            className="last:border-b-0"
            value={prettyPrintEnum(fxTrade.operation)}
          />
          <DetailRow
            label="Sell amount"
            className="last:border-b-0"
            value={<CurrencyAmountUI {...fxTrade.sellAmount} />}
          />
          <DetailRow
            label="Buy amount"
            className="last:border-b-0"
            value={<CurrencyAmountUI {...fxTrade.buyAmount} />}
          />
          <DetailRow
            label="Contract rate"
            className="last:border-b-0"
            value={
              <div className="flex flex-col">
                <div>
                  1 {fxTrade.buyCurrency} = {fxTrade.buyToSellRate.toFixed()}{" "}
                  {fxTrade.sellCurrency}
                </div>
                <div>
                  1 {fxTrade.sellCurrency} = {fxTrade.sellToBuyRate.toFixed()}{" "}
                  {fxTrade.buyCurrency}
                </div>
              </div>
            }
          />

          {markToMarket && (
            <>
              <DetailRow
                label="Spot rate"
                className="last:border-b-0"
                value={
                  <div className="flex flex-col">
                    <div>
                      1 {fxTrade.buyCurrency} ={" "}
                      {markToMarket.latestBuyToSellRate.rate.toFixed()}{" "}
                      {fxTrade.sellCurrency}
                    </div>
                    <div>
                      1 {fxTrade.sellCurrency} ={" "}
                      {markToMarket.latestSellToBuyRate.rate.toFixed()}{" "}
                      {fxTrade.buyCurrency}
                    </div>
                  </div>
                }
              />

              <DetailRow
                label="Mark to market"
                className="last:border-b-0"
                value={
                  <div>
                    <CurrencyAmountUI ca={markToMarket.buyAmount} />
                    <CurrencyAmountUI ca={markToMarket.sellAmount} />
                  </div>
                }
              />
            </>
          )}

          <DetailRow
            label="Fee amount"
            className="last:border-b-0"
            value={
              fxTrade.recordedRevenue ? (
                <CurrencyAmountUI {...fxTrade.recordedRevenue} />
              ) : (
                "-"
              )
            }
          />

          {fxHedge && (
            <DetailRow
              label="Margin balance"
              className="last:border-b-0"
              value={<CurrencyAmountUI ca={fxHedge.marginAmount} />}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 overflow-hidden">
          <div className="flex items-center justify-between">
            <Txt variant="card-title">Transfer Groups</Txt>
            <LinkTransferGroupModal fxTradeId={fxTrade.id} />
          </div>

          {(fxTrade.transferGroupLinks ?? []).length === 0 ? (
            <p className="pb-4 text-sm text-tertiary">
              No transfer groups linked to this trade.
            </p>
          ) : (
            <DataTable
              data={fxTrade.transferGroupLinks ?? []}
              columns={transferGroupLinkColumns}
              manualSorting={false}
              manualPagination={true}
              hidePagination={true}
              showCsvExport={false}
              pageSize={10_000}
              className="overflow-hidden border"
              cellClassName="text-sm p-2"
              onRowClick={(row) => {
                const tgId = row.original.transferGroup?.id;
                if (tgId) {
                  navigate({
                    to: "/transfer-groups/$transferGroupId",
                    params: { transferGroupId: tgId },
                  });
                }
              }}
            />
          )}
        </div>

        {marginTransactions !== undefined && (
          <div className="flex flex-col gap-2 overflow-hidden">
            <Txt variant="card-title">Margin transactions</Txt>

            {(marginTransactions ?? []).length === 0 ? (
              <p className="pb-4 text-sm text-tertiary">
                No margin transactions recorded
              </p>
            ) : (
              <DataTable
                data={marginTransactions ?? []}
                columns={marginTransactionColumns}
                manualSorting={false}
                manualPagination={true}
                hidePagination={true}
                showCsvExport={false}
                pageSize={10_000}
                className="overflow-hidden border"
                cellClassName="text-sm p-2"
                onRowClick={(row) => {
                  navigate({
                    to: "/transactions/$transactionId",
                    params: { transactionId: row.original.id },
                  });
                }}
              />
            )}
          </div>
        )}

        {fxTrade.__typename === "CurrencycloudFXTrade" && (
          <div className="rounded-md border p-4">
            <div className="flex w-full items-start justify-between">
              <h3 className="font-semibold font-title">
                Currencycloud trade details
              </h3>
            </div>

            <DetailRow
              label="Trade ID"
              className="last:border-b-0"
              value={fxTrade.externalId ?? "None"}
            />

            {(() => {
              const eburyTradeErrors = error?.graphQLErrors?.filter(
                (gqlError) => {
                  return (
                    gqlError.path?.at(0) === "fxTrade" &&
                    gqlError.path?.at(1) === "currencycloudTradeDetails"
                  );
                },
              );
              if (eburyTradeErrors && eburyTradeErrors.length > 0) {
                return (
                  <DetailRow
                    label="Trade status"
                    value={
                      <StatusBase
                        label="Failed to load Currencycloud trade"
                        icon={AlertCircle}
                        color="error"
                      />
                    }
                  />
                );
              }
              if (fxTrade.currencycloudTradeDetails === null) {
                return (
                  <DetailRow
                    label="Trade status"
                    className="last:border-b-0"
                    value="Currencycloud trade not found"
                  />
                );
              }
              if (fxTrade.currencycloudTradeDetails === undefined) {
                return (
                  <DetailRow
                    label="Trade status"
                    className="last:border-b-0"
                    value={
                      <div className="flex items-center gap-2">
                        <Spinner size="md" />
                        Loading Currencycloud trade details
                      </div>
                    }
                  />
                );
              }

              return (
                <>
                  <DetailRow
                    label="Trade status"
                    className="last:border-b-0"
                    value={fxTrade.currencycloudTradeDetails.status}
                  />
                  <DetailRow
                    label="Short reference"
                    className="last:border-b-0"
                    value={fxTrade.currencycloudTradeDetails.shortReference}
                  />
                  <DetailRow
                    label="Mid market rate"
                    className="last:border-b-0"
                    value={new Decimal(
                      fxTrade.currencycloudTradeDetails.midMarketRate,
                    ).toFixed()}
                  />
                  <DetailRow
                    label="Core rate"
                    className="last:border-b-0"
                    value={new Decimal(
                      fxTrade.currencycloudTradeDetails.coreRate,
                    ).toFixed()}
                  />
                  <DetailRow
                    label="Partner rate"
                    className="last:border-b-0"
                    value={new Decimal(
                      fxTrade.currencycloudTradeDetails.partnerRate,
                    ).toFixed()}
                  />
                  <DetailRow
                    label="Client rate"
                    className="last:border-b-0"
                    value={new Decimal(
                      fxTrade.currencycloudTradeDetails.clientRate,
                    ).toFixed()}
                  />
                </>
              );
            })()}
          </div>
        )}

        {fxTrade.__typename === "EburyFXTrade" && (
          <div className="flex flex-col gap-2 overflow-hidden">
            <Txt variant="card-title">Ebury trade details</Txt>
            <div className="rounded-md border p-4">
              <DetailRow
                label="Ebury Trade ID"
                className="last:border-b-0"
                value={fxTrade.externalId ?? "None"}
              />
              {(() => {
                const eburyTradeErrors = error?.graphQLErrors?.filter(
                  (gqlError) => {
                    return (
                      gqlError.path?.at(0) === "fxTrade" &&
                      gqlError.path?.at(1) === "eburyFXTrade"
                    );
                  },
                );
                if (eburyTradeErrors && eburyTradeErrors.length > 0) {
                  return (
                    <DetailRow
                      label="Trade status"
                      value={
                        <StatusBase
                          label="Failed to load Ebury trade"
                          icon={AlertCircle}
                          color="error"
                        />
                      }
                    />
                  );
                }
                if (!fxTrade.eburyTradeDetails) {
                  return (
                    <DetailRow
                      label="Trade status"
                      className="last:border-b-0"
                      value="Ebury trade not found"
                    />
                  );
                }

                return (
                  <>
                    <DetailRow
                      label="Trade status"
                      className="last:border-b-0"
                      value={fxTrade.eburyTradeDetails.status}
                    />
                    <DetailRow
                      label="Maturity date"
                      className="last:border-b-0"
                      value={(() => {
                        const { shortDate, fullTime, timeZone } =
                          formatDateComponentsInTimeZone({
                            date: new Date(
                              fxTrade.eburyTradeDetails.maturityDate,
                            ),
                            timeZone: getOpsTimezone(),
                          });

                        return (
                          <div className="flex flex-col items-start gap-4">
                            <div className="flex items-center gap-3">
                              <div>
                                {shortDate} {fullTime}
                              </div>
                              <div className="text-tertiary">({timeZone})</div>
                            </div>
                            {fxTrade.eburyTradeDetails.status !== "Closed" && (
                              <Alert
                                kind="warning"
                                description="Funds must be received in the source account by this time"
                              />
                            )}
                          </div>
                        );
                      })()}
                    />
                    <DetailRow
                      label={`${fxTrade.sellCurrency} to ${fxTrade.buyCurrency} rate`}
                      className="last:border-b-0"
                      value={fxTrade.sellToBuyRate.toString()}
                    />
                    <DetailRow
                      label={`${fxTrade.buyCurrency} to ${fxTrade.sellCurrency} rate`}
                      className="last:border-b-0"
                      value={fxTrade.buyToSellRate.toString()}
                    />
                    <DetailRow
                      label="Buy amount"
                      className="last:border-b-0"
                      value={
                        <CurrencyAmountUI
                          amount={new Decimal(fxTrade.buyAmount.amount)}
                          currency={fxTrade.buyAmount.currency}
                        />
                      }
                    />
                    <DetailRow
                      label="Sell amount"
                      className="last:border-b-0"
                      value={
                        <CurrencyAmountUI
                          amount={new Decimal(fxTrade.sellAmount.amount)}
                          currency={fxTrade.sellAmount.currency}
                        />
                      }
                    />
                    <DetailRow
                      label="Fee amount"
                      className="last:border-b-0"
                      value={
                        <CurrencyAmountUI
                          amount={
                            new Decimal(
                              fxTrade.eburyTradeDetails.feeAmount.amount,
                            )
                          }
                          currency={
                            fxTrade.eburyTradeDetails.feeAmount.currency
                          }
                        />
                      }
                    />

                    {forward && (
                      <>
                        <DetailRow
                          label="Margin call"
                          className="last:border-b-0"
                          value={
                            <CurrencyAmountUI
                              amount={new Decimal(forward.marginCall.amount)}
                              currency={forward.marginCall.currency}
                            />
                          }
                        />
                        <DetailRow
                          label="Initial margin"
                          className="last:border-b-0"
                          value={
                            <CurrencyAmountUI
                              amount={new Decimal(forward.initialMargin.amount)}
                              currency={forward.initialMargin.currency}
                            />
                          }
                        />
                        <DetailRow
                          label="Buy balance for drawdown"
                          className="last:border-b-0"
                          value={
                            <CurrencyAmountUI
                              amount={
                                new Decimal(
                                  forward.buyBalanceForDrawdown.amount,
                                )
                              }
                              currency={forward.buyBalanceForDrawdown.currency}
                            />
                          }
                        />
                        <DetailRow
                          label="Sell balance for drawdown"
                          className="last:border-b-0"
                          value={
                            <CurrencyAmountUI
                              amount={
                                new Decimal(
                                  forward.sellBalanceForDrawdown.amount,
                                )
                              }
                              currency={forward.sellBalanceForDrawdown.currency}
                            />
                          }
                        />
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="mt-16 border-b pb-2">
          <Txt variant="card-title">Event log</Txt>
        </div>

        <div>
          {fxTrade.events && (
            <TimeLog entries={fxTrade.events.map(entryFromEventFragment)} />
          )}
          {(fxTrade.events?.length ?? 0) === 0 && (
            <div className="text-center">No events</div>
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

const FXTradeActionButton: FC<{
  fxTradeId: string;
  status: FxTradeStatus;
  triggerButtonLabel: string;
  confirmTitle: string;
  confirmDescription: string;
  disabled?: boolean;
}> = ({
  fxTradeId,
  status,
  triggerButtonLabel,
  confirmTitle,
  confirmDescription,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [suppressNotifications, setSuppressNotifications] = useState(false);

  const [updateFXTradeStatusMutation, { loading }] = useMutation(
    graphql(`mutation FXTradeDetail_UpdateStatus($input: UpdateFXTradeStatusInput!) {
      updateFXTradeStatus(input: $input) {
        fxTrade {
          ...AdminFXTradeDetail
        }
      }
    }`),
  );

  const handleClick = useCallback(async () => {
    const defaultErrorMessage =
      "An unexpected error occurred. Please try again later.";
    await updateFXTradeStatusMutation({
      variables: {
        input: {
          fxTradeId,
          status,
          suppressNotifications,
        },
      },
      onCompleted: (data) => {
        if (!data.updateFXTradeStatus?.fxTrade?.status) {
          throw new Error("expected updateFXTradeStatus status");
        }
        toast({
          kind: "success",
          autoHide: true,
          title: "FX trade status updated",
          description: `FX trade status updated to '${data.updateFXTradeStatus.fxTrade.status}'`,
        });

        setOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || defaultErrorMessage;
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to update FX trade status",
          description: errorMessage,
        });
      },
    });
  }, [status, fxTradeId, updateFXTradeStatusMutation, suppressNotifications]);

  return (
    <Dialog onOpenChange={setOpen} {...{ open }}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          disabled={disabled}
          onSelect={(e) => e.preventDefault()}
        >
          {triggerButtonLabel}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-4 sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{confirmTitle}</DialogTitle>
          <DialogDescription>{confirmDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Checkbox
            id="suppressNotifications"
            name="convert"
            onCheckedChange={(v) => setSuppressNotifications(v === true)}
          />
          <Label htmlFor="suppressNotifications">
            Do not send notification
          </Label>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            leftIcon={<RiArrowLeftFill />}
            type="button"
            variant="secondary"
            onClick={(e) => {
              setOpen(false);
              e.preventDefault();
            }}
          >
            {" "}
            Back
          </Button>
          <Button disabled={loading} onClick={handleClick}>
            {loading ? "Saving..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LinkTransferGroupModal: FC<{ fxTradeId: string }> = ({ fxTradeId }) => {
  const [open, setOpen] = useState(false);
  const [transferGroupId, setTransferGroupId] = useState("");
  const [kind, setKind] = useState<FxTradeTransferGroupKind | "">("");

  const [linkMutation, { loading }] = useMutation(
    graphql(`
      mutation FXTradeDetail_LinkTransferGroup(
        $input: LinkFxTradeToTransferGroupInput!
      ) {
        linkFxTradeToTransferGroup(input: $input) {
          fxTradeTransferGroupLink {
            id
          }
        }
      }
    `),
    { refetchQueries: ["FXTradeDetailPage"] },
  );

  const handleSubmit = useCallback(async () => {
    if (!transferGroupId || !kind) {
      return;
    }
    await linkMutation({
      variables: { input: { fxTradeId, transferGroupId, kind } },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: "Transfer group linked",
          description: "The transfer group has been linked to this FX trade.",
        });
        setOpen(false);
        setTransferGroupId("");
        setKind("");
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to link transfer group",
          description:
            error.graphQLErrors?.[0]?.message ??
            "An unexpected error occurred.",
        });
      },
    });
  }, [fxTradeId, transferGroupId, kind, linkMutation]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Link transfer group
        </Button>
      </DialogTrigger>
      <DialogContent className="flex flex-col gap-4 sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Link transfer group</DialogTitle>
          <DialogDescription>
            Link an existing transfer group to this FX trade.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="transferGroupId">Transfer group ID</Label>
            <Input
              id="transferGroupId"
              placeholder="transfer_group_..."
              value={transferGroupId}
              onChange={(e) => setTransferGroupId(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kind">Kind</Label>
            <Select
              value={kind}
              onValueChange={(v) => setKind(v as FxTradeTransferGroupKind)}
            >
              <SelectTrigger id="kind">
                <SelectValue placeholder="Select a kind" />
              </SelectTrigger>
              <SelectContent>
                {fXTradeTransferGroupKindValues.map((k) => (
                  <SelectItem key={k} value={k}>
                    {prettyPrintEnum(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            leftIcon={<RiArrowLeftFill />}
            type="button"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            {" "}
            Back
          </Button>
          <Button
            disabled={loading || !transferGroupId || !kind}
            onClick={handleSubmit}
          >
            {loading ? <Spinner /> : "Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const transferGroupLinkColumns: ColumnDef<FxTradeTransferGroupLinkDetailFragment>[] =
  [
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Requested" />
      ),
      cell: ({ row }) => {
        const tg = row.original.transferGroup;
        if (!tg) {
          return null;
        }
        const { shortDate, shortTime } = formatLocalDateComponents(
          tg.createdAt,
        );
        return (
          <div className="flex flex-col">
            <div>{shortDate}</div>
            <div className="text-tertiary text-xs">{shortTime}</div>
          </div>
        );
      },
    },
    {
      id: "linkKind",
      header: "Link Kind",
      cell: ({ row }) => (
        <LabelPill label={prettyPrintEnum(row.original.kind ?? "")} />
      ),
    },
    {
      id: "createdByUser",
      header: "Created By",
      cell: ({ row }) => {
        const createdByUser = row.original.transferGroup?.createdByUser;
        if (!createdByUser) {
          return null;
        }
        return <UserProfile {...createdByUser} />;
      },
    },
    {
      id: "description",
      header: "Description",
      cell: ({ row }) => {
        const tg = row.original.transferGroup;
        if (!tg) {
          return null;
        }
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-tertiary text-xs uppercase">
              {prettyPrintEnum(tg.kind)}
            </span>
            <div>
              <TransferGroupDescription transferGroup={tg} />
            </div>
          </div>
        );
      },
    },
    {
      id: "totalVolume",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Volume"
          align="right"
          className="flex flex-row justify-end pr-2"
        />
      ),
      cell: ({ row }) => {
        const tg = row.original.transferGroup;
        if (!tg?.totalVolume) {
          return null;
        }
        const { amount, currency } = tg.totalVolume;
        const itemCount =
          (tg.transfers?.length ?? 0) + (tg.payouts?.length ?? 0);
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <CurrencyAmountUI amount={amount} currency={currency} />({itemCount}
            )
          </div>
        );
      },
    },
    {
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.transferGroup?.status;
        if (!status) {
          return null;
        }
        return <StatusUI status={status} />;
      },
    },
  ];

const marginTransactionColumns: ColumnDef<FxTradeMarginTransactionFragment>[] =
  [
    {
      accessorKey: "effectiveAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Effective at" />
      ),
      cell: ({ row }) => {
        const { shortDate, shortTime } = formatLocalDateComponents(
          row.original.effectiveAt,
        );
        return (
          <div className="flex flex-col">
            <div>{shortDate}</div>
            <div className="text-tertiary text-xs">{shortTime}</div>
          </div>
        );
      },
    },
    {
      id: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Amount"
          align="right"
          className="flex flex-row justify-end pr-2"
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <CurrencyAmountUI ca={row.original.currencyAmount} />
            <DirectionIcon direction={row.original.direction} />
          </div>
        );
      },
    },
    {
      id: "description",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Description"
          align="left"
          className="flex flex-row pr-2"
        />
      ),
      cell: ({ row }) => {
        return row.original.description;
      },
    },
  ];
